import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Switch,
    Button,
    Alert,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { updateDeduplication, getGlobalConfig } from '../../services/api';

const MODE_OPTIONS = [
    {
        value: 'prefer_physical_by_data_item',
        label: 'Prefer Physical by Data Item',
        description: 'Keep one row per report/data item and prefer the row that has a Physical Name.',
    },
    {
        value: 'key_fields',
        label: 'Composite Key Fields',
        description: 'Deduplicate using the configured key fields such as Physical, SQL, Expression, and Layout.',
    },
];

const SHARED_SYSTEM_OPTIONS = [
    { key: 'dedup_exact_report', label: 'Deduplicate exact rows per report (all columns)' },
    { key: 'dedup_exact_global', label: 'Deduplicate exact rows globally (all columns)' },
    { key: 'drop_missing_physical', label: 'Drop rows missing Physical Name' },
    { key: 'drop_missing_layout', label: 'Drop rows missing Report Layout' },
];

const KEY_MODE_OPTIONS = [
    { key: 'include_table_logical_name', label: 'Include table logical name in deduplication keys' },
    { key: 'include_table_physical_name', label: 'Include table physical name in deduplication keys' },
    { key: 'include_sql_filter', label: 'Include SQL (Report SQL / Filter) in key-based deduplication' },
    { key: 'include_expression', label: 'Include Expression in key-based deduplication' },
];

const LEGACY_DEFAULTS = {
    dedup_prefer_physical_by_data_item: true,
    dedup_by_key: false,
    dedup_exact_report: false,
    dedup_exact_global: false,
    include_table_logical_name: false,
    include_table_physical_name: false,
    include_sql_filter: true,
    include_expression: false,
    drop_missing_physical: true,
    drop_missing_layout: true,
};

const CATALYST_DEFAULTS = {
    dedup_prefer_physical_by_data_item: true,
    dedup_by_key: false,
    dedup_exact_report: false,
    dedup_exact_global: false,
    include_table_logical_name: false,
    include_table_physical_name: false,
    include_sql_filter: true,
    include_expression: false,
    drop_missing_physical: false,
    drop_missing_layout: false,
    fill_missing_physical: false,
};

function normalizeSystemSettings(settings, defaults) {
    const normalized = { ...defaults, ...(settings || {}) };
    if (
        Object.prototype.hasOwnProperty.call(settings || {}, 'dedup_by_key')
        && !Object.prototype.hasOwnProperty.call(settings || {}, 'dedup_prefer_physical_by_data_item')
    ) {
        normalized.dedup_prefer_physical_by_data_item = false;
    }
    if (normalized.dedup_prefer_physical_by_data_item) {
        normalized.dedup_by_key = false;
    } else if (normalized.dedup_by_key) {
        normalized.dedup_prefer_physical_by_data_item = false;
    } else {
        normalized.dedup_prefer_physical_by_data_item = true;
    }
    return normalized;
}

function getModeValue(settings) {
    return settings?.dedup_prefer_physical_by_data_item ? 'prefer_physical_by_data_item' : 'key_fields';
}

function DeduplicationConfig({ deduplication, onUpdate, showNotification }) {
    const [legacySettings, setLegacySettings] = useState(LEGACY_DEFAULTS);
    const [catalystSettings, setCatalystSettings] = useState(CATALYST_DEFAULTS);
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);

    const description = deduplication?.description || 'Controls how lineage uploads are deduplicated and cleaned.';

    const mergedLegacy = useMemo(() => ({
        ...normalizeSystemSettings(deduplication?.legacy, LEGACY_DEFAULTS),
    }), [deduplication]);

    const mergedCatalyst = useMemo(() => ({
        ...normalizeSystemSettings(deduplication?.catalyst, CATALYST_DEFAULTS),
    }), [deduplication]);

    useEffect(() => {
        setLegacySettings(mergedLegacy);
        setCatalystSettings(mergedCatalyst);
        setHasChanges(false);
    }, [mergedLegacy, mergedCatalyst]);

    const handleToggle = (system, key) => (event) => {
        const checked = event.target.checked;
        if (system === 'legacy') {
            setLegacySettings((prev) => ({ ...prev, [key]: checked }));
        } else {
            setCatalystSettings((prev) => ({ ...prev, [key]: checked }));
        }
        setHasChanges(true);
    };

    const handleModeChange = (system) => (event) => {
        const mode = event.target.value;
        const nextState = (prev) => ({
            ...prev,
            dedup_prefer_physical_by_data_item: mode === 'prefer_physical_by_data_item',
            dedup_by_key: mode === 'key_fields',
        });
        if (system === 'legacy') {
            setLegacySettings(nextState);
        } else {
            setCatalystSettings(nextState);
        }
        setHasChanges(true);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await updateDeduplication({
                description,
                legacy: legacySettings,
                catalyst: catalystSettings,
            });
            const config = await getGlobalConfig();
            onUpdate(config);
            setHasChanges(false);
            showNotification('Deduplication settings saved');
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to save', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setLegacySettings(mergedLegacy);
        setCatalystSettings(mergedCatalyst);
        setHasChanges(false);
    };

    const renderSystemSettings = (system, settings) => {
        const isKeyMode = getModeValue(settings) === 'key_fields';
        return (
            <>
                <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
                    <FormLabel component="legend" sx={{ mb: 1, color: 'text.primary', fontWeight: 600 }}>
                        Deduplication Mode
                    </FormLabel>
                    <RadioGroup value={getModeValue(settings)} onChange={handleModeChange(system)}>
                        {MODE_OPTIONS.map((option) => (
                            <Box key={option.value} sx={{ mb: 1 }}>
                                <FormControlLabel
                                    value={option.value}
                                    control={<Radio />}
                                    label={option.label}
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
                                    {option.description}
                                </Typography>
                            </Box>
                        ))}
                    </RadioGroup>
                </FormControl>

                {SHARED_SYSTEM_OPTIONS.map((option) => (
                    <FormControlLabel
                        key={option.key}
                        sx={{ display: 'flex', mr: 0, mb: 1 }}
                        control={
                            <Switch
                                checked={!!settings[option.key]}
                                onChange={handleToggle(system, option.key)}
                                disabled={!isKeyMode}
                            />
                        }
                        label={option.label}
                    />
                ))}

                {KEY_MODE_OPTIONS.map((option) => (
                    <FormControlLabel
                        key={option.key}
                        sx={{ display: 'flex', mr: 0, mb: 1 }}
                        control={
                            <Switch
                                checked={!!settings[option.key]}
                                onChange={handleToggle(system, option.key)}
                                disabled={!isKeyMode}
                            />
                        }
                        label={option.label}
                    />
                ))}
            </>
        );
    };

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h6" fontWeight={600}>
                        Deduplication
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {description}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {hasChanges && (
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={handleReset}
                        >
                            Reset
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>
            </Box>

            {hasChanges && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    You have unsaved changes. Click "Save Changes" to persist them.
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                            Legacy
                        </Typography>
                        {renderSystemSettings('legacy', legacySettings)}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                            Catalyst
                        </Typography>
                        {renderSystemSettings('catalyst', catalystSettings)}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default DeduplicationConfig;
